// Captura de leads da LP /fisiocompleto.
//
// Diferença em relação aos outros funis: o lead NUNCA é enviado ao RD CRM.
// A passagem para a vendedora acontece pelo próprio link do WhatsApp (nimble)
// no fim da captura, para leads MORNO/QUENTE.
import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";

import { nomeField, emailField, telefoneField, trackingString } from "@/lib/leads/validation";
import { buildLeadTags, conversionIdentifierFor } from "@/lib/leads/taxonomy";
import { extractClientNetwork, runPostCapture } from "@/lib/leads/postprocess.server";
import { qualifyByAnswers, EXCLUDE_TAG } from "@/lib/leads/qualification";

const AREA = "combo";
const POS_NOME = "Projeto Fisio Completo";
const NO_CRM_REASON = "não enviado ao CRM (funil /fisiocompleto envia direto ao WhatsApp)";

const RespostaSchema = z.object({
  pergunta: z.string().max(300),
  resposta: z.string().max(300),
  pontos: z.number().int().min(0).max(10),
});

const ContactSchema = z.object({
  page_url: trackingString(2000).optional().default(""),
  nome: nomeField,
  email: emailField,
  telefone: telefoneField,
  utm_source: trackingString(400).optional().default(""),
  utm_medium: trackingString(400).optional().default(""),
  utm_campaign: trackingString(400).optional().default(""),
});

const LeadSchema = z.object({
  partial_lead_id: z.string().uuid().optional(),
  page_url: trackingString(2000).optional().default(""),
  nome: nomeField,
  email: emailField,
  telefone: telefoneField,
  respostas: z.array(RespostaSchema).max(10),
  score_total: z.number().int().min(0).max(100),
  score_pct: z.number().int().min(0).max(100),
  classificacao: z.enum(["quente", "morno", "frio"]),
  utm_source: trackingString(400).optional().default(""),
  utm_medium: trackingString(400).optional().default(""),
  utm_campaign: trackingString(400).optional().default(""),
});

export const captureFisioCompletoContact = createServerFn({ method: "POST" })
  .inputValidator((input: unknown) => ContactSchema.parse(input))
  .handler(async ({ data }) => {
    const { assertPublicCaptureOrigin } = await import("@/lib/leads/public-capture-guard.server");
    assertPublicCaptureOrigin();
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { client_ip, user_agent } = await extractClientNetwork();

    const conversionIdentifier = conversionIdentifierFor({ source: "fisiocompleto", partial: true });
    const rdTags = buildLeadTags({ source: "fisiocompleto", partial: true });

    const { sendLeadToRdStationMarketing } = await import("@/lib/leads/rdstation.server");
    const rdRes = await sendLeadToRdStationMarketing({
      source: "fisiocompleto",
      conversionIdentifier,
      tags: rdTags,
      nome: data.nome,
      email: data.email,
      telefone: data.telefone,
      area: AREA,
      pos_nome: POS_NOME,
      respostas: [],
      page_url: data.page_url,
      utm_source: data.utm_source,
      utm_medium: data.utm_medium,
      utm_campaign: data.utm_campaign,
    });

    const payload = {
      nome: data.nome,
      email: data.email.toLowerCase(),
      telefone: data.telefone,
      pos_recomendada: null,
      pos_nome: POS_NOME,
      pos_url: null,
      score_total: 0,
      respostas: [],
      utm_source: data.utm_source || null,
      utm_medium: data.utm_medium || null,
      utm_campaign: data.utm_campaign || null,
      origem: conversionIdentifier,
      area: AREA,
      classificacao: null,
      delivered_to_crm: false,
      delivered_to_rdstation: rdRes.delivered,
      rdstation_error: rdRes.error,
      crm_error: NO_CRM_REASON,
    };

    // Sempre cria um registro novo: uma captura pública não pode sobrescrever
    // um lead existente apenas por conhecer o e-mail dele.
    let leadId: string | null = null;
    let saveError: string | null = null;
    const { data: inserted, error } = await supabaseAdmin
      .from("leads")
      .insert(payload)
      .select("id")
      .single();
    if (error) saveError = error.message;
    else leadId = inserted.id as string;

    await runPostCapture({
      source: "fisiocompleto",
      mode: "partial",
      endpoint: "fn:fisiocompleto",
      area: AREA,
      posNome: POS_NOME,
      nome: data.nome,
      email: data.email,
      telefone: data.telefone,
      status: leadId ? "ok" : "db_error",
      http_status: leadId ? 200 : 500,
      leadId,
      error: saveError,
      pageUrl: data.page_url || null,
      utm_source: data.utm_source || null,
      utm_medium: data.utm_medium || null,
      utm_campaign: data.utm_campaign || null,
      client_ip,
      user_agent,
    });

    return { ok: !!leadId, id: leadId, delivered: rdRes.delivered };
  });

export const submitFisioCompletoLead = createServerFn({ method: "POST" })
  .inputValidator((input: unknown) => LeadSchema.parse(input))
  .handler(async ({ data }) => {
    const { assertPublicCaptureOrigin } = await import("@/lib/leads/public-capture-guard.server");
    assertPublicCaptureOrigin();
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { client_ip, user_agent } = await extractClientNetwork();

    const conversionIdentifier = conversionIdentifierFor({ source: "fisiocompleto" });
    // Pontuação e classificação recalculadas no servidor a partir das respostas.
    // O valor enviado pelo navegador só pode rebaixar o lead, nunca promovê-lo.
    const serverTotal = data.respostas.reduce((s, r) => s + (r.pontos ?? 0), 0);
    const serverMax = data.respostas.length * 2;
    const serverPct = serverMax > 0 ? Math.min(100, Math.round((serverTotal / serverMax) * 100)) : 0;
    const rank = { frio: 0, morno: 1, quente: 2 } as const;
    const computed: "quente" | "morno" | "frio" =
      serverPct >= 75 ? "quente" : serverPct >= 50 ? "morno" : "frio";
    const fallback = rank[data.classificacao] < rank[computed] ? data.classificacao : computed;
    data.score_total = Math.min(100, serverTotal);
    data.score_pct = serverPct;
    const { classificacao, discarded } = qualifyByAnswers(data.respostas, fallback);
    const rdTags = buildLeadTags({ source: "fisiocompleto", classificacao });
    const rdTagsFinal = discarded ? [...rdTags, EXCLUDE_TAG] : rdTags;

    const { sendLeadToRdStationMarketing } = await import("@/lib/leads/rdstation.server");
    const rdRes = await sendLeadToRdStationMarketing({
      source: "fisiocompleto",
      conversionIdentifier,
      tags: rdTagsFinal,
      nome: data.nome,
      email: data.email,
      telefone: data.telefone,
      area: AREA,
      pos_nome: POS_NOME,
      classificacao,
      score_total: data.score_total,
      score_pct: data.score_pct,
      respostas: data.respostas,
      page_url: data.page_url,
      utm_source: data.utm_source,
      utm_medium: data.utm_medium,
      utm_campaign: data.utm_campaign,
    });

    let leadId: string | null = null;
    let saveError: string | null = null;
    try {
      const leadPayload = {
        nome: data.nome,
        email: data.email.toLowerCase(),
        telefone: data.telefone,
        pos_recomendada: null,
        pos_nome: POS_NOME,
        pos_url: null,
        score_total: data.score_total,
        respostas: data.respostas,
        utm_source: data.utm_source || null,
        utm_medium: data.utm_medium || null,
        utm_campaign: data.utm_campaign || null,
        origem: conversionIdentifier,
        area: AREA,
        classificacao,
        delivered_to_crm: false,
        delivered_to_rdstation: rdRes.delivered,
        crm_deal_id: null,
        crm_stage_id: null,
        crm_seller_email: null,
        crm_error: NO_CRM_REASON,
        rdstation_error: rdRes.error,
      };
      const { resolveOwnedPartialLeadId } = await import("@/lib/leads/partial-lead.server");
      const ownedPartialId = await resolveOwnedPartialLeadId(data.partial_lead_id, data.email);
      if (ownedPartialId) {
        const { error } = await supabaseAdmin
          .from("leads")
          .update(leadPayload)
          .eq("id", ownedPartialId);
        if (error) saveError = error.message;
        else leadId = ownedPartialId;
      } else {
        const { data: inserted, error } = await supabaseAdmin
          .from("leads")
          .insert(leadPayload)
          .select("id")
          .single();
        if (error) saveError = error.message;
        else leadId = inserted.id as string;
      }
    } catch (err) {
      saveError = err instanceof Error ? err.message : String(err);
    }

    await runPostCapture({
      source: "fisiocompleto",
      mode: "final",
      endpoint: "fn:fisiocompleto",
      area: AREA,
      posNome: POS_NOME,
      nome: data.nome,
      email: data.email,
      telefone: data.telefone,
      status: leadId ? "ok" : "db_error",
      http_status: leadId ? 200 : 500,
      leadId,
      error: saveError,
      pageUrl: data.page_url || null,
      utm_source: data.utm_source || null,
      utm_medium: data.utm_medium || null,
      utm_campaign: data.utm_campaign || null,
      client_ip,
      user_agent,
    });

    return {
      ok: true,
      id: leadId,
      delivered: rdRes.delivered,
      classificacao,
      aprovado: classificacao === "morno" || classificacao === "quente",
    };
  });
